/* @jsxImportSource @emotion/react */
import React from 'react'
import classNames from 'classnames'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import type { SanityTextContentProps } from '@slite/components'
import { Button, Pill, SanityTextRenderer } from '@slite/components'
import { DotIcon } from '@slite/icons'

import { Section } from '../../components/Section'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { Data } from './+data'
import { slugifyTitle } from './slugify'

export default function SliteChangelogPage() {
  const { changelogEntries } = useData<Data>()
  const intl = useIntl()
  const routes = useRoutes()

  const pageTitle = intl.formatMessage({
    id: 'website.changelog.helmet.title',
    defaultMessage: "What's new in Slite",
  })
  const pageDescription = intl.formatMessage({
    id: 'website.changelog.helmet.description',
    defaultMessage: 'See the latest updates to Slite',
  })
  return (
    <SlitePage
      title={pageTitle}
      description={pageDescription}
      ogImage={
        'https://cdn.sanity.io/images/dnsgqii5/production/7988028858e7139446fa88271ef5b7bc7d31d015-2400x1260.jpg'
      }
      ogTitle={pageTitle}
      ogDescription={pageDescription}
      canonicalUrl={routes.changelog.index()}
    >
      <Section
        containerTopPadding={72}
        containerBottomPadding={100}
        className="position-relative text-align-center"
      >
        <div className="grid-12">
          <div className="grid-span-10-large grid-offset-1-large grid-span-12 flexbox flexbox-column flexbox-align-center">
            <WebsiteText variant="heroTitle" className="margin-bottom-12">
              <FormattedMessage
                id="website.changelog.hero.title"
                defaultMessage="Changelog"
              />
            </WebsiteText>
            <div className="flexbox flexbox-align-center flexbox-gap-8 margin-bottom-24">
              <a
                href={routes.external.linkedin.url}
                className={classNames(
                  'font-size-6 shade-30 shade-10-on-hover font-weight-semibold'
                )}
              >
                <FormattedMessage
                  id="website.changelog.hero.link.linkedin"
                  defaultMessage="Follow us on Linkedin"
                />
              </a>
              <DotIcon className="shade-40" size={12} />
              <a
                href={routes.external.twitter.url}
                className={classNames(
                  'font-size-6 shade-30 shade-10-on-hover font-weight-semibold'
                )}
              >
                <FormattedMessage
                  id="website.changelog.hero.link.twitter"
                  defaultMessage="Follow us on X"
                />
              </a>
            </div>
            <a href={routes.external.community.url}>
              <Button variant="secondary" size="medium" rounded>
                <FormattedMessage
                  id="website.changelog.hero.cta"
                  defaultMessage="Join the community"
                />
              </Button>
            </a>
          </div>
        </div>
      </Section>
      <Section
        background="shade-100"
        containerTopPadding={0}
        containerBottomPadding={120}
      >
        {changelogEntries.map(
          ({ title, date, coverImage, content, product }, index) => {
            const teaserContent = content.slice(0, 6)
            const detailHref = routes.changelog.detail({
              slug: slugifyTitle(title),
            })
            return (
              <div key={index}>
                <div className="grid-12 position-relative">
                  {index > 0 && (
                    <div className="grid-span-10-large grid-offset-1-large grid-span-12-medium grid-offset-0-medium grid-span-12-small grid-offset-0-small  border-top-small border-shade-45 margin-vertical-60" />
                  )}
                  <div className="grid-span-3-large grid-offset-1-large grid-span-2-medium grid-offset-0-medium grid-span-12-small grid-offset-0-small padding-right-24 margin-bottom-16">
                    <div
                      className="line-height-large shade-30 font-size-large"
                      style={{ position: 'sticky', top: 100 }}
                    >
                      <FormattedDate
                        value={date}
                        month="short"
                        day="numeric"
                        year="numeric"
                      />
                    </div>
                  </div>
                  <div className="grid-span-7-large grid-span-9-medium grid-span-12-small ">
                    {coverImage && (
                      <a href={detailHref}>
                        <img
                          src={coverImage?.url ?? undefined}
                          style={{
                            objectFit: 'cover',
                            objectPosition: 'center top',
                          }}
                          alt=""
                          className="width-full radius-large margin-bottom-16"
                        />
                      </a>
                    )}
                    <div className="simple-rich-text large simple-rich-text-minimal-table flexbox flexbox-column width-full">
                      <h2 style={{ marginTop: 6, marginBottom: 16 }}>
                        <a
                          href={detailHref}
                          style={{
                            textDecoration: 'none',
                            color: 'var(--shade-05)',
                          }}
                        >
                          {title}
                        </a>
                        {product === 'super' && (
                          <a
                            style={{
                              width: 'fit-content',
                            }}
                            href={routes.aiSearch()}
                          >
                            <Pill
                              className="margin-left-8"
                              style={{
                                height: 32,
                                lineHeight: '32px',
                                padding: '0 12px',
                              }}
                            >
                              <FormattedMessage
                                id="website.changelog.entry.super"
                                defaultMessage="AI-search (super)"
                              />
                            </Pill>
                          </a>
                        )}
                      </h2>
                      <div
                        style={{
                          maxHeight: 180,
                          overflow: 'hidden',
                          WebkitMaskImage:
                            'linear-gradient(to bottom, black 55%, transparent 100%)',
                          maskImage:
                            'linear-gradient(to bottom, black 55%, transparent 100%)',
                        }}
                      >
                        <SanityTextRenderer
                          variant="website"
                          content={teaserContent as SanityTextContentProps}
                        />
                      </div>
                      <div className="margin-top-16">
                        <a href={detailHref}>
                          <Button
                            rounded
                            variant="primary-high-contrast"
                            size="medium"
                          >
                            <FormattedMessage
                              id="website.changelog.entry.readMore"
                              defaultMessage="Read more"
                            />
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }
        )}
      </Section>
    </SlitePage>
  )
}
